import React, { useState } from 'react';
import { subDays } from 'date-fns';
import { Layout } from '@/components/Layout';
import { ActivationFunnel } from '@/components/admin/analytics/ActivationFunnel';
import { LiquidityMetricsCards } from '@/components/admin/analytics/LiquidityMetricsCards';
import { VendorLeaderboardTable } from '@/components/admin/analytics/VendorLeaderboardTable';
import { RetentionCohortChart } from '@/components/admin/analytics/RetentionCohortChart';
import { DateRangeSelector } from '@/components/reports/spend/DateRangeSelector';
import {
  useBuyerActivationFunnel,
  useVendorActivationFunnel,
  useMarketplaceLiquidity,
  useVendorLeaderboard,
  useRetentionCohorts,
} from '@/hooks/useMarketplaceAnalytics';
import { BarChart3, FlaskConical } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

export default function AdminAnalyticsPage() {
  const [startDate, setStartDate] = useState<Date>(() => subDays(new Date(), 30));
  const [endDate, setEndDate] = useState<Date>(() => new Date());
  const [showTestData, setShowTestData] = useState(false);

  const dateRange = { startDate, endDate };
  const analyticsOptions = { includeTestData: showTestData };

  const handleDateChange = (start: Date, end: Date) => {
    setStartDate(start);
    setEndDate(end);
  };

  const { data: buyerFunnel, isLoading: buyerLoading } = useBuyerActivationFunnel(dateRange, analyticsOptions);
  const { data: vendorFunnel, isLoading: vendorLoading } = useVendorActivationFunnel(dateRange, analyticsOptions);
  const { data: liquidity, isLoading: liquidityLoading } = useMarketplaceLiquidity(dateRange, analyticsOptions);
  const { data: leaderboard, isLoading: leaderboardLoading } = useVendorLeaderboard(dateRange, analyticsOptions);
  const { data: buyerRetention, isLoading: buyerRetentionLoading } = useRetentionCohorts('client', analyticsOptions);
  const { data: vendorRetention, isLoading: vendorRetentionLoading } = useRetentionCohorts('vendor', analyticsOptions);

  return (
    <Layout>
      <div className="p-6 space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <BarChart3 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Marketplace Analytics</h1>
              <p className="text-muted-foreground">
                Monitor marketplace health, user activation, and retention metrics
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FlaskConical className={`h-4 w-4 ${showTestData ? 'text-primary' : 'text-muted-foreground'}`} />
            <Switch
              id="show-test-data"
              checked={showTestData}
              onCheckedChange={setShowTestData}
            />
            <Label htmlFor="show-test-data" className="text-sm text-muted-foreground">
              Show Test Data
            </Label>
          </div>
        </div>

        {/* Date Range Selector */}
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
        />

        {/* Section 1: Activation Funnels */}
        <div className="grid gap-6 lg:grid-cols-2">
          <ActivationFunnel
            title="Buyer Activation Funnel"
            description="Track buyer journey from signup to repeat purchase"
            stages={buyerFunnel || []}
            isLoading={buyerLoading}
            variant="buyer"
          />
          <ActivationFunnel
            title="Vendor Activation Funnel"
            description="Track vendor journey from signup to multiple wins"
            stages={vendorFunnel || []}
            isLoading={vendorLoading}
            variant="vendor"
          />
        </div>

        {/* Section 2: Liquidity Metrics */}
        <LiquidityMetricsCards
          avgQuotesPerRfq={liquidity?.avgQuotesPerRfq ?? 0}
          rfqsWith3PlusQuotes={liquidity?.rfqsWith3PlusQuotes ?? 0}
          rfqsWith3PlusQuotesPercent={liquidity?.rfqsWith3PlusQuotesPercent ?? 0}
          avgTimeToFirstQuoteHours={liquidity?.avgTimeToFirstQuoteHours ?? 0}
          rfqsWithAward={liquidity?.rfqsWithAward ?? 0}
          rfqsWithAwardPercent={liquidity?.rfqsWithAwardPercent ?? 0}
          totalRfqs={liquidity?.totalRfqs ?? 0}
          isLoading={liquidityLoading}
        />

        {/* Section 3: Vendor Leaderboard */}
        <VendorLeaderboardTable
          vendors={leaderboard || []}
          isLoading={leaderboardLoading}
        />

        {/* Section 4: Retention Cohorts */}
        <div className="grid gap-6 lg:grid-cols-2">
          <RetentionCohortChart
            title="Buyer Retention"
            description="Monthly cohort retention analysis for buyers"
            cohorts={buyerRetention || []}
            isLoading={buyerRetentionLoading}
          />
          <RetentionCohortChart
            title="Vendor Retention"
            description="Monthly cohort retention analysis for vendors"
            cohorts={vendorRetention || []}
            isLoading={vendorRetentionLoading}
          />
        </div>
      </div>
    </Layout>
  );
}
